import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useForm, Controller, useWatch } from 'react-hook-form';
import CurrencyInput from 'react-currency-input-field';
import Alert from '../../../components/Alert/Alert';
import { getDepartments, getSalesUsers } from '../api/salesTargetApi';
import useModalEffect from '../../../hooks/useModalEffect';

const SalesTargetForm = memo(({ isOpen, onClose, onSubmit, initialData }) => {
  // ✅ PERFORMANCE: Use React Hook Form for optimized form state management
  const { control, handleSubmit, reset, setValue, setError, getValues, watch, formState: { errors } } = useForm({
    defaultValues: {
      year: new Date().getFullYear(),
      departmentId: '',
      amount: '',
      details: [] // [{ userId, userName, amount, contractWonAmount, hasMonthlyBreakdown, monthlyBreakdown: {1: amount, 2: amount, ...} }]
    },
    mode: 'onBlur', // Only validate on blur, not on every keystroke
    shouldUnregister: false // Keep form values even when fields unmount
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const isInitializingRef = useRef(false);
  const [departments, setDepartments] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);

  useModalEffect(isOpen);

  // ✅ PERFORMANCE: Handle currency input change - only update local state
  const handleCurrencyChange = useCallback((name, value) => {
    setValue(name, value || '', { shouldValidate: false });
  }, [setValue]);

  // Load departments and sales users when modal opens
  useEffect(() => {
    const loadMeta = async () => {
      if (!isOpen) return;
      try {
        const [deptRes, salesRes] = await Promise.all([
          getDepartments(),
          getSalesUsers()
        ]);
        setDepartments(Array.isArray(deptRes) ? deptRes : []);
        setSalesUsers(Array.isArray(salesRes) ? salesRes : []);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error loading departments or sales users', e);
      }
    };
    loadMeta();
  }, [isOpen]);

  // Helpers & watchers
  const cleanNumber = (v) => {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    const num = parseFloat(String(v).replace(/[^\d]/g, ''));
    return Number.isNaN(num) ? 0 : num;
  };

  const selectedDepartmentId = useWatch({ control, name: 'departmentId' });
  const amountValue = useWatch({ control, name: 'amount' });
  const detailsValue = useWatch({ control, name: 'details' });

  const departmentSales = selectedDepartmentId
    ? salesUsers.filter(u => String(u.departmentId) === String(selectedDepartmentId))
    : [];

  // ✅ PERFORMANCE: Handle edit mode - reset form with editing data
  useEffect(() => {
    const initializeEditData = async () => {
      if (isOpen && initialData && !isInitializingRef.current) {
        isInitializingRef.current = true;

        const transformedData = {
          year: initialData.year || new Date().getFullYear(),
          departmentId: initialData.departmentId || '',
          amount: initialData.amount ? initialData.amount.toString() : '',
          details: Array.isArray(initialData.details)
            ? initialData.details.map(d => ({
                userId: d.userId,
                userName: d.userName,
                amount: d.amount ? d.amount.toString() : '',
                contractWonAmount: d.contractWonAmount ? d.contractWonAmount.toString() : '',
                hasMonthlyBreakdown: d.hasMonthlyBreakdown || false,
                monthlyBreakdown: d.monthlyBreakdown || {},
                hasContractWonMonthlyBreakdown: d.hasContractWonMonthlyBreakdown || false,
                contractWonMonthlyBreakdown: d.contractWonMonthlyBreakdown || {}
              }))
            : []
        };

        // If editing and there is a department but no details yet, show one empty row by default
        if (
          transformedData.departmentId &&
          (!transformedData.details || transformedData.details.length === 0)
        ) {
          transformedData.details = [
            {
              userId: null, userName: '', amount: '', contractWonAmount: '',
              hasMonthlyBreakdown: false, monthlyBreakdown: {},
              hasContractWonMonthlyBreakdown: false, contractWonMonthlyBreakdown: {}
            }
          ];
        }

        reset(transformedData);
        isInitializingRef.current = false;
      } else if (isOpen && !initialData && !isInitializingRef.current) {
        isInitializingRef.current = true;
        
        // Reset form with default values
        const formValues = {
          year: new Date().getFullYear(),
          departmentId: '',
          amount: '',
          details: []
        };
        
        reset(formValues);
        isInitializingRef.current = false;
      }
    };

    initializeEditData();
  }, [isOpen, initialData?.id, reset]);

  // React to department change when creating new (reset details for that department)
  useEffect(() => {
    if (!isOpen) return;
    // Only auto-populate when creating new (not editing)
    if (!initialData && selectedDepartmentId && departmentSales.length > 0) {
      const rows = departmentSales.map(u => ({
        userId: u.id,
        userName: u.name,
        amount: '',
        contractWonAmount: '',
        hasMonthlyBreakdown: false,
        monthlyBreakdown: {},
        hasContractWonMonthlyBreakdown: false,
        contractWonMonthlyBreakdown: {}
      }));
      setValue('details', rows, { shouldValidate: false });
    }
    // Only clear when creating new (not editing)
    if (!initialData && !selectedDepartmentId) {
      setValue('details', [], { shouldValidate: false });
    }
  }, [selectedDepartmentId, departmentSales.length, isOpen, initialData, setValue]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any remaining modal states when component unmounts
      const cleanupModal = () => {
        // Remove modal scroll lock
      };
      
      cleanupModal();
    };
  }, []);


  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 3000);
  };

  const handleClose = () => {
    
    
    // Reset form when closing
    reset({
      year: new Date().getFullYear(),
      departmentId: '',
      amount: '',
      details: []
    });
    isInitializingRef.current = false;
    if (onClose) onClose();
  };

  const validateForm = (data) => {
    const errors = {};
    const deptTarget = cleanNumber(data.amount);
    const salesTotal = (data.details || []).reduce(
      (sum, d) => sum + cleanNumber(d.amount),
      0
    );

    if (!data.year || data.year < 2000 || data.year > 2100) {
      errors.year = 'Year is required and must be between 2000 and 2100';
    }
    if (!deptTarget || deptTarget <= 0) {
      errors.amount = 'Amount is required and must be greater than 0';
    }
    if (!data.departmentId) {
      errors.departmentId = 'Department is required';
    }
    if (salesTotal > deptTarget) {
      errors.details = `Total sales targets (${salesTotal.toLocaleString('id-ID')}) cannot exceed department target (${deptTarget.toLocaleString('id-ID')})`;
    }

    // Validate monthly breakdowns
    (data.details || []).forEach((detail, idx) => {
      if (detail.hasMonthlyBreakdown) {
        const annualAmount = cleanNumber(detail.amount);
        const monthlyBreakdown = detail.monthlyBreakdown || {};
        const monthlyTotal = Object.values(monthlyBreakdown).reduce(
          (sum, val) => sum + cleanNumber(val), 0
        );
        
        if (monthlyTotal !== annualAmount) {
          errors.details = `Monthly total (${monthlyTotal.toLocaleString('id-ID')}) must equal annual target (${annualAmount.toLocaleString('id-ID')}) for sales person #${idx + 1}`;
        }
      }

      if (detail.hasContractWonMonthlyBreakdown) {
        const annualContractWonAmount = cleanNumber(detail.contractWonAmount);
        const contractWonMonthlyBreakdown = detail.contractWonMonthlyBreakdown || {};
        const contractWonMonthlyTotal = Object.values(contractWonMonthlyBreakdown).reduce(
          (sum, val) => sum + cleanNumber(val), 0
        );

        if (contractWonMonthlyTotal !== annualContractWonAmount) {
          errors.details = `Contract won monthly total (${contractWonMonthlyTotal.toLocaleString('id-ID')}) must equal annual contract won target (${annualContractWonAmount.toLocaleString('id-ID')}) for sales person #${idx + 1}`;
        }
      }
    });

    return errors;
  };

  // ✅ PERFORMANCE: Handle form submit - prepare data and call parent onSubmit
  const onSubmitForm = useCallback(async (data) => {
    if (isSubmitting) {
      return;
    }

    const validationErrors = validateForm(data);
    
    if (Object.keys(validationErrors).length > 0) {
      // Set errors in form state using react-hook-form's setError
      Object.keys(validationErrors).forEach(key => {
        setError(key, {
          type: 'manual',
          message: validationErrors[key]
        });
      });
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare payload
      const payload = {
        year: parseInt(data.year, 10),
        departmentId: data.departmentId ? parseInt(data.departmentId, 10) : null,
        amount: cleanNumber(data.amount),
        details: (data.details || [])
          .filter(d => d.userId && cleanNumber(d.amount) > 0)
          .map(d => {
            const detail = {
              userId: d.userId,
              amount: cleanNumber(d.amount),
              contractWonAmount: cleanNumber(d.contractWonAmount),
              hasMonthlyBreakdown: d.hasMonthlyBreakdown || false,
              hasContractWonMonthlyBreakdown: d.hasContractWonMonthlyBreakdown || false
            };
            
            // Add monthly breakdown if enabled
            if (d.hasMonthlyBreakdown && d.monthlyBreakdown) {
              const monthlyBreakdown = {};
              Object.entries(d.monthlyBreakdown).forEach(([month, amount]) => {
                const cleanedAmount = cleanNumber(amount);
                if (cleanedAmount > 0) {
                  monthlyBreakdown[month] = cleanedAmount;
                }
              });
              detail.monthlyBreakdown = monthlyBreakdown;
            }

            // Add contract won monthly breakdown if enabled
            if (d.hasContractWonMonthlyBreakdown && d.contractWonMonthlyBreakdown) {
              const contractWonMonthlyBreakdown = {};
              Object.entries(d.contractWonMonthlyBreakdown).forEach(([month, amount]) => {
                const cleanedAmount = cleanNumber(amount);
                if (cleanedAmount > 0) {
                  contractWonMonthlyBreakdown[month] = cleanedAmount;
                }
              });
              detail.contractWonMonthlyBreakdown = contractWonMonthlyBreakdown;
            }

            return detail;
          })
      };

      // Handle both promise and non-promise returns
      const result = onSubmit(payload);
      
      if (result && typeof result.then === 'function') {
        await result;
      }

      // Reset form after successful submission
      reset({
        year: new Date().getFullYear(),
        departmentId: '',
        amount: '',
        details: []
      });
      handleClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to save sales target';
      showAlert('danger', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, onSubmit, reset, setError, handleClose]);

  // Generate year options (current year ± 10 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 10; i <= currentYear + 10; i++) {
    yearOptions.push(i);
  }

  return (
    <>
      <style>{`
        #modal-create-sales-target .inv-section-label {
          font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #6c7a7d; margin-bottom: 0.75rem;
          display: flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-sales-target .inv-section-label::after {
          content: ''; flex: 1; height: 1px; background: #e9ecef;
        }
        #modal-create-sales-target .inv-footer {
          border-top: 1px solid #e9ecef !important;
          background: #fff;
          padding: 1rem 1.5rem;
          display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem;
          flex-shrink: 0;
        }
        #modal-create-sales-target .btn-inv-save {
          display: inline-flex; align-items: center; gap: 0.4rem;
        }
        #modal-create-sales-target .btn-inv-save:disabled {
          opacity: 0.6; cursor: not-allowed;
        }
        #modal-create-sales-target .inv-required-star { color: #e53935; margin-left: 2px; }
      `}</style>
      {isOpen && ReactDOM.createPortal(<div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />, document.body)}
      <div
        className={`modal modal-blur fade${isOpen ? ' show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}
        id="modal-create-sales-target"
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-md modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {initialData ? 'Edit Sales Target' : 'Create Sales Target'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={isSubmitting}
              ></button>
            </div>

            {alert && (
              <div className="modal-body pb-0">
                <Alert
                  type={alert.type}
                  message={alert.message}
                  onClose={() => setAlert(null)}
                />
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmitForm, (errors) => {
              console.log('React Hook Form validation errors:', errors);
              const formValues = getValues();
              onSubmitForm(formValues);
            })}>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  Target Details
                </div>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <Controller
                      name="year"
                      control={control}
                      rules={{ 
                        required: 'Year is required',
                        validate: (value) => {
                          if (!value || value < 2000 || value > 2100) {
                            return 'Year must be between 2000 and 2100';
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <>
                          <label className="form-label required">Tahun Target</label>
                          <select
                            {...field}
                            className={`form-select ${errors.year ? 'is-invalid' : ''}`}
                            disabled={isSubmitting}
                          >
                            <option value="">Select Year</option>
                            {yearOptions.map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                          {errors.year && (
                            <div className="invalid-feedback">{errors.year.message}</div>
                          )}
                        </>
                      )}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Controller
                      name="departmentId"
                      control={control}
                      rules={{
                        required: 'Department is required'
                      }}
                      render={({ field }) => (
                        <>
                          <label className="form-label required">Service Category</label>
                          <select
                            {...field}
                            className={`form-select ${errors.departmentId ? 'is-invalid' : ''}`}
                            disabled={isSubmitting}
                          >
                            <option value="">Select option</option>
                            {departments.map(dep => (
                              <option key={dep.id || dep.value} value={dep.id || dep.value}>
                                {dep.name || dep.label}
                              </option>
                            ))}
                          </select>
                          {errors.departmentId && (
                            <div className="invalid-feedback">{errors.departmentId.message}</div>
                          )}
                        </>
                      )}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <Controller
                      name="amount"
                      control={control}
                      rules={{ 
                        required: 'Amount is required',
                        validate: (value) => {
                          if (!value || parseFloat(value) <= 0) {
                            return 'Amount must be greater than 0';
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <>
                          <label className="form-label required">Nilai Target Departemen (IDR)</label>
                          <CurrencyInput
                            className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                            name={field.name}
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              handleCurrencyChange('amount', value);
                            }}
                            placeholder="0"
                            decimalsLimit={0}
                            prefix="Rp "
                            allowNegativeValue={false}
                            disabled={isSubmitting}
                          />
                          {errors.amount && (
                            <div className="invalid-feedback">{errors.amount.message}</div>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-body">
                <div className="inv-section-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-8H7v8M7 3v5h8"/><path d="M3 3h18v18H3z"/></svg>
                  Sales Breakdown
                </div>
                {/* Per-sales breakdown (always shown) */}
                <div className="row mt-3">
                  <div className="col-12">
                    {errors.details && (
                      <div className="text-danger mb-2 small">
                        {errors.details.message || errors.details}
                      </div>
                    )}

                    {!selectedDepartmentId && (
                      <div className="text-muted mb-2 small">
                        Please select Service Category first to add Sales person targets.
                      </div>
                    )}

                    {selectedDepartmentId && (detailsValue || []).map((row, idx) => (
                      <div className="row mb-3" key={row.userId || idx}>
                        <div className="col-md-6">
                          <label className="form-label required">Sales person</label>
                          <select
                            className="form-select"
                            value={row.userId || ''}
                            disabled={isSubmitting}
                            onChange={(e) => {
                              const value = e.target.value;
                              const userId = value ? parseInt(value, 10) : null;
                              const user = departmentSales.find(
                                u => String(u.id) === String(value)
                              );
                              const current = detailsValue || [];
                              const updated = [...current];
                              updated[idx] = {
                                ...updated[idx],
                                userId,
                                userName: user ? user.name : ''
                              };
                              setValue('details', updated, { shouldValidate: false });
                            }}
                          >
                            <option value="">Select option</option>
                            {departmentSales.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label required">Nilai Target (IDR)</label>
                          <CurrencyInput
                            className="form-control"
                            value={row.amount}
                            prefix="Rp "
                            decimalsLimit={0}
                            allowNegativeValue={false}
                            disabled={isSubmitting}
                            onValueChange={(val) => {
                              const current = detailsValue || [];
                              const updated = [...current];
                              updated[idx] = {
                                ...updated[idx],
                                amount: val || ''
                              };
                              setValue('details', updated, { shouldValidate: false });
                            }}
                          />
                        </div>
                        <div className="col-md-6 mt-2">
                          <label className="form-label">Contract Won Target (IDR)</label>
                          <CurrencyInput
                            className="form-control"
                            value={row.contractWonAmount}
                            prefix="Rp "
                            decimalsLimit={0}
                            allowNegativeValue={false}
                            disabled={isSubmitting}
                            placeholder="0"
                            onValueChange={(val) => {
                              const current = detailsValue || [];
                              const updated = [...current];
                              updated[idx] = {
                                ...updated[idx],
                                contractWonAmount: val || ''
                              };
                              setValue('details', updated, { shouldValidate: false });
                            }}
                          />
                        </div>

                        {/* Monthly Breakdown Toggle */}
                        <div className="col-12 mt-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`monthly-${idx}`}
                              checked={row.hasMonthlyBreakdown || false}
                              disabled={isSubmitting}
                              onChange={(e) => {
                                const current = detailsValue || [];
                                const updated = [...current];
                                const isEnabled = e.target.checked;
                                
                                // Initialize monthly breakdown with equal distribution if enabling
                                let monthlyBreakdown = updated[idx].monthlyBreakdown || {};
                                if (isEnabled && Object.keys(monthlyBreakdown).length === 0) {
                                  const annualAmount = cleanNumber(updated[idx].amount);
                                  const monthlyAmount = Math.floor(annualAmount / 12);
                                  const remainder = annualAmount - (monthlyAmount * 12);
                                  
                                  monthlyBreakdown = {};
                                  for (let m = 1; m <= 12; m++) {
                                    // Add remainder to last month
                                    monthlyBreakdown[m] = (m === 12 ? monthlyAmount + remainder : monthlyAmount).toString();
                                  }
                                }
                                
                                updated[idx] = {
                                  ...updated[idx],
                                  hasMonthlyBreakdown: isEnabled,
                                  monthlyBreakdown: isEnabled ? monthlyBreakdown : {}
                                };
                                setValue('details', updated, { shouldValidate: false });
                              }}
                            />
                            <label className="form-check-label" htmlFor={`monthly-${idx}`}>
                              Enable Monthly Target Breakdown
                            </label>
                          </div>
                        </div>

                        {/* Monthly Breakdown Grid */}
                        {row.hasMonthlyBreakdown && (
                          <div className="col-12 mt-3">
                            <div className="card">
                              <div className="card-header">
                                <h4 className="card-title">Monthly Target Distribution</h4>
                              </div>
                              <div className="card-body">
                                <div className="row g-2">
                                  {[
                                    { num: 1, name: 'January' }, { num: 2, name: 'February' }, { num: 3, name: 'March' },
                                    { num: 4, name: 'April' }, { num: 5, name: 'May' }, { num: 6, name: 'June' },
                                    { num: 7, name: 'July' }, { num: 8, name: 'August' }, { num: 9, name: 'September' },
                                    { num: 10, name: 'October' }, { num: 11, name: 'November' }, { num: 12, name: 'December' }
                                  ].map(month => (
                                    <div className="col-md-4 col-sm-6" key={month.num}>
                                      <label className="form-label small">{month.name}</label>
                                      <CurrencyInput
                                        className="form-control form-control-sm"
                                        value={row.monthlyBreakdown?.[month.num] || ''}
                                        prefix="Rp "
                                        decimalsLimit={0}
                                        allowNegativeValue={false}
                                        disabled={isSubmitting}
                                        placeholder="0"
                                        onValueChange={(val) => {
                                          const current = detailsValue || [];
                                          const updated = [...current];
                                          const monthlyBreakdown = { ...(updated[idx].monthlyBreakdown || {}) };
                                          monthlyBreakdown[month.num] = val || '';
                                          updated[idx] = {
                                            ...updated[idx],
                                            monthlyBreakdown
                                          };
                                          setValue('details', updated, { shouldValidate: false });
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Monthly Summary */}
                                <div className="mt-3 small">
                                  {(() => {
                                    const annualTarget = cleanNumber(row.amount);
                                    const monthlyBreakdown = row.monthlyBreakdown || {};
                                    const monthlyTotal = Object.values(monthlyBreakdown).reduce(
                                      (sum, val) => sum + cleanNumber(val), 0
                                    );
                                    const diff = annualTarget - monthlyTotal;
                                    const isValid = diff === 0;
                                    
                                    return (
                                      <div className={isValid ? 'text-success' : 'text-danger'}>
                                        <strong>Monthly Total:</strong> {monthlyTotal.toLocaleString('id-ID')} / 
                                        <strong> Annual Target:</strong> {annualTarget.toLocaleString('id-ID')}
                                        {!isValid && (
                                          <span className="ms-2">
                                            ({diff > 0 ? 'Remaining' : 'Over by'} {Math.abs(diff).toLocaleString('id-ID')})
                                          </span>
                                        )}
                                        {isValid && <span className="ms-2">✓ Valid</span>}
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* Quick Actions */}
                                <div className="mt-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-2"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                      const current = detailsValue || [];
                                      const updated = [...current];
                                      const annualAmount = cleanNumber(updated[idx].amount);
                                      const monthlyAmount = Math.floor(annualAmount / 12);
                                      const remainder = annualAmount - (monthlyAmount * 12);
                                      
                                      const monthlyBreakdown = {};
                                      for (let m = 1; m <= 12; m++) {
                                        monthlyBreakdown[m] = (m === 12 ? monthlyAmount + remainder : monthlyAmount).toString();
                                      }
                                      
                                      updated[idx] = {
                                        ...updated[idx],
                                        monthlyBreakdown
                                      };
                                      setValue('details', updated, { shouldValidate: false });
                                    }}
                                  >
                                    Distribute Equally
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                      const current = detailsValue || [];
                                      const updated = [...current];
                                      updated[idx] = {
                                        ...updated[idx],
                                        monthlyBreakdown: {}
                                      };
                                      setValue('details', updated, { shouldValidate: false });
                                    }}
                                  >
                                    Clear All
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Contract Won Monthly Breakdown Toggle */}
                        <div className="col-12 mt-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`contract-won-monthly-${idx}`}
                              checked={row.hasContractWonMonthlyBreakdown || false}
                              disabled={isSubmitting}
                              onChange={(e) => {
                                const current = detailsValue || [];
                                const updated = [...current];
                                const isEnabled = e.target.checked;

                                // Initialize monthly breakdown with equal distribution if enabling
                                let contractWonMonthlyBreakdown = updated[idx].contractWonMonthlyBreakdown || {};
                                if (isEnabled && Object.keys(contractWonMonthlyBreakdown).length === 0) {
                                  const annualAmount = cleanNumber(updated[idx].contractWonAmount);
                                  const monthlyAmount = Math.floor(annualAmount / 12);
                                  const remainder = annualAmount - (monthlyAmount * 12);

                                  contractWonMonthlyBreakdown = {};
                                  for (let m = 1; m <= 12; m++) {
                                    // Add remainder to last month
                                    contractWonMonthlyBreakdown[m] = (m === 12 ? monthlyAmount + remainder : monthlyAmount).toString();
                                  }
                                }

                                updated[idx] = {
                                  ...updated[idx],
                                  hasContractWonMonthlyBreakdown: isEnabled,
                                  contractWonMonthlyBreakdown: isEnabled ? contractWonMonthlyBreakdown : {}
                                };
                                setValue('details', updated, { shouldValidate: false });
                              }}
                            />
                            <label className="form-check-label" htmlFor={`contract-won-monthly-${idx}`}>
                              Enable Monthly Contract Won Target Breakdown
                            </label>
                          </div>
                        </div>

                        {/* Contract Won Monthly Breakdown Grid */}
                        {row.hasContractWonMonthlyBreakdown && (
                          <div className="col-12 mt-3">
                            <div className="card">
                              <div className="card-header">
                                <h4 className="card-title">Monthly Contract Won Target Distribution</h4>
                              </div>
                              <div className="card-body">
                                <div className="row g-2">
                                  {[
                                    { num: 1, name: 'January' }, { num: 2, name: 'February' }, { num: 3, name: 'March' },
                                    { num: 4, name: 'April' }, { num: 5, name: 'May' }, { num: 6, name: 'June' },
                                    { num: 7, name: 'July' }, { num: 8, name: 'August' }, { num: 9, name: 'September' },
                                    { num: 10, name: 'October' }, { num: 11, name: 'November' }, { num: 12, name: 'December' }
                                  ].map(month => (
                                    <div className="col-md-4 col-sm-6" key={month.num}>
                                      <label className="form-label small">{month.name}</label>
                                      <CurrencyInput
                                        className="form-control form-control-sm"
                                        value={row.contractWonMonthlyBreakdown?.[month.num] || ''}
                                        prefix="Rp "
                                        decimalsLimit={0}
                                        allowNegativeValue={false}
                                        disabled={isSubmitting}
                                        placeholder="0"
                                        onValueChange={(val) => {
                                          const current = detailsValue || [];
                                          const updated = [...current];
                                          const contractWonMonthlyBreakdown = { ...(updated[idx].contractWonMonthlyBreakdown || {}) };
                                          contractWonMonthlyBreakdown[month.num] = val || '';
                                          updated[idx] = {
                                            ...updated[idx],
                                            contractWonMonthlyBreakdown
                                          };
                                          setValue('details', updated, { shouldValidate: false });
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>

                                {/* Monthly Summary */}
                                <div className="mt-3 small">
                                  {(() => {
                                    const annualTarget = cleanNumber(row.contractWonAmount);
                                    const contractWonMonthlyBreakdown = row.contractWonMonthlyBreakdown || {};
                                    const monthlyTotal = Object.values(contractWonMonthlyBreakdown).reduce(
                                      (sum, val) => sum + cleanNumber(val), 0
                                    );
                                    const diff = annualTarget - monthlyTotal;
                                    const isValid = diff === 0;

                                    return (
                                      <div className={isValid ? 'text-success' : 'text-danger'}>
                                        <strong>Monthly Total:</strong> {monthlyTotal.toLocaleString('id-ID')} /
                                        <strong> Annual Target:</strong> {annualTarget.toLocaleString('id-ID')}
                                        {!isValid && (
                                          <span className="ms-2">
                                            ({diff > 0 ? 'Remaining' : 'Over by'} {Math.abs(diff).toLocaleString('id-ID')})
                                          </span>
                                        )}
                                        {isValid && <span className="ms-2">✓ Valid</span>}
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* Quick Actions */}
                                <div className="mt-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-secondary me-2"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                      const current = detailsValue || [];
                                      const updated = [...current];
                                      const annualAmount = cleanNumber(updated[idx].contractWonAmount);
                                      const monthlyAmount = Math.floor(annualAmount / 12);
                                      const remainder = annualAmount - (monthlyAmount * 12);

                                      const contractWonMonthlyBreakdown = {};
                                      for (let m = 1; m <= 12; m++) {
                                        contractWonMonthlyBreakdown[m] = (m === 12 ? monthlyAmount + remainder : monthlyAmount).toString();
                                      }

                                      updated[idx] = {
                                        ...updated[idx],
                                        contractWonMonthlyBreakdown
                                      };
                                      setValue('details', updated, { shouldValidate: false });
                                    }}
                                  >
                                    Distribute Equally
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={isSubmitting}
                                    onClick={() => {
                                      const current = detailsValue || [];
                                      const updated = [...current];
                                      updated[idx] = {
                                        ...updated[idx],
                                        contractWonMonthlyBreakdown: {}
                                      };
                                      setValue('details', updated, { shouldValidate: false });
                                    }}
                                  >
                                    Clear All
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="col-12 text-end mt-1">
                          <button
                            type="button"
                            className="btn btn-link text-danger btn-sm"
                            disabled={isSubmitting}
                            onClick={() => {
                              const current = detailsValue || [];
                              const updated = current.filter((_, i) => i !== idx);
                              setValue('details', updated, { shouldValidate: false });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}

                    {selectedDepartmentId && (!detailsValue || detailsValue.length === 0) && (
                      <div className="text-muted mb-2 small">
                        No Sales person added yet.
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-secondary mt-2"
                      disabled={isSubmitting || !selectedDepartmentId}
                      onClick={() => {
                        const current = detailsValue || [];
                        const updated = [
                          ...current,
                          {
                            userId: null, userName: '', amount: '', contractWonAmount: '',
                            hasMonthlyBreakdown: false, monthlyBreakdown: {},
                            hasContractWonMonthlyBreakdown: false, contractWonMonthlyBreakdown: {}
                          }
                        ];
                        setValue('details', updated, { shouldValidate: false });
                      }}
                    >
                      + Add Sales person
                    </button>

                    {/* Summary row */}
                    <div className="mt-2 small">
                      {(() => {
                        const deptTarget = cleanNumber(amountValue);
                        const salesTotal = (detailsValue || []).reduce(
                          (sum, d) => sum + cleanNumber(d.amount),
                          0
                        );
                        const diff = deptTarget - salesTotal;
                        const over = salesTotal > deptTarget;
                        return (
                          <span className={over ? 'text-danger' : 'text-muted'}>
                            Sales total: {salesTotal.toLocaleString('id-ID')} / Department target:{' '}
                            {deptTarget.toLocaleString('id-ID')} ({over ? 'Over by ' : 'Remaining '}
                            {Math.abs(diff).toLocaleString('id-ID')})
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="inv-footer">
                <span className="text-muted me-auto" style={{ fontSize: '0.75rem' }}>
                  {isSubmitting ? 'Saving sales target…' : (
                    <span style={{ color: '#9aa5b1' }}>
                      <span className="inv-required-star">*</span> Required fields
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-inv-save"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: 14, height: 14, borderWidth: 2 }} />
                      Saving…
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
});

SalesTargetForm.displayName = 'SalesTargetForm';

SalesTargetForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default SalesTargetForm;
